import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FormsModule } from '@angular/forms';
import { Highlight } from 'ngx-highlightjs';
import { DataService } from '../../services/data.service';
import { ProjectGet } from '../../models/project/project-get.model';
import { DataSourceGet } from '../../models/datasource';

export interface StartProjectDialogData {
  project: ProjectGet;
  dataSource: DataSourceGet | null;
}

export type StartProjectAction = 'start-with-existing' | 'upload-new-file' | 'cancel';

export interface StartProjectResult {
  action: StartProjectAction;
  file?: File;
}

@Component({
  selector: 'app-start-project-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatButtonToggleModule,
    FormsModule,
    Highlight
  ],
  templateUrl: './start-project-dialog.component.html',
  styleUrls: ['./start-project-dialog.component.scss']
})
export class StartProjectDialogComponent implements OnInit {
  isApiListener = false;
  isFileSource = false;
  endpoint = '';
  token = '';
  tokenJustCopied = false;
  endpointJustCopied = false;
  codeJustCopied = false;
  isRegeneratingToken = false;
  tokenRegenerationError: string | null = null;
  selectedFile: File | null = null;
  loading = false;
  selectedApiCall: 'start' | 'status' | 'results' = 'start';
  statusEndpoint = '';
  resultsEndpoint = '';
  private placeholderToken = '••••••••-••••-••••-••••-••••••••••••';

  constructor(
    public dialogRef: MatDialogRef<StartProjectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StartProjectDialogData,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    // Determine the type of data source
    if (this.data.dataSource) {
      this.isApiListener = this.data.dataSource.dataSourceTypeId === 1;
      this.isFileSource = [2, 3, 4, 5].includes(this.data.dataSource.dataSourceTypeId);
      
      if (this.isApiListener) {
        // For API Listeners, set up the project clean data endpoint
        const baseUrl = this.dataService.getBaseUrl();
        this.endpoint = `${baseUrl}/Project/clean_data/${this.data.project.projectId}/${this.data.dataSource.dataSourceId}`;
        this.statusEndpoint = `${baseUrl}/LongRunningAction/status/{ACTION_TOKEN}`;
        this.resultsEndpoint = `${baseUrl}/LongRunningAction/result/{ACTION_TOKEN}`;
        
        // Check if project has a token
        if (this.data.project.isTokenPresent) {
          // Token exists but we don't have the value, show placeholder
          this.token = this.placeholderToken;
        } else {
          this.token = this.placeholderToken;
        }
      }
    }
  }

  isTokenAvailable(): boolean {
    return this.token !== this.placeholderToken && this.token !== '';
  }

  regenerateToken(): void {
    if (!this.data.project?.projectId) {
      console.error('No project ID available for token regeneration');
      return;
    }

    this.isRegeneratingToken = true;
    this.tokenRegenerationError = null;

    this.dataService.getProjectToken(this.data.project.projectId).subscribe({
      next: (newToken) => {
        this.token = newToken;
        this.isRegeneratingToken = false;
        console.log('Project token regenerated successfully:', newToken);
        
        // Automatically copy the new token to clipboard
        this.copyToClipboard(newToken, 'token');
      },
      error: (error) => {
        this.isRegeneratingToken = false;
        this.tokenRegenerationError = error.message || 'Failed to regenerate token';
        console.error('Error regenerating token:', error);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' } as StartProjectResult);
  }

  onStartWithExisting(): void {
    this.dialogRef.close({ action: 'start-with-existing' } as StartProjectResult);
  }

  browseFiles(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = this.getFileAcceptTypes();
    input.onchange = (event) => this.onFileSelected(event);
    input.click();
  }

  getFileAcceptTypes(): string {
    if (!this.data.dataSource) return '*';
    
    switch (this.data.dataSource.dataSourceTypeId) {
      case 2: return '.xlsx,.xls'; // Excel
      case 3: return '.csv,.txt';  // CSV
      case 4: return '.parquet';   // Parquet
      case 5: return '.json';      // JSON
      default: return '*';
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files?.[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  onUploadNewFile(): void {
    if (!this.selectedFile) {
      return;
    }
    
    this.dialogRef.close({ 
      action: 'upload-new-file',
      file: this.selectedFile
    } as StartProjectResult);
  }

  copyToClipboard(text: string, type: 'endpoint' | 'token' | 'code'): void {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'token') {
        this.tokenJustCopied = true;
        setTimeout(() => {
          this.tokenJustCopied = false;
        }, 2000);
      } else if (type === 'endpoint') {
        this.endpointJustCopied = true;
        setTimeout(() => {
          this.endpointJustCopied = false;
        }, 2000);
      } else if (type === 'code') {
        this.codeJustCopied = true;
        setTimeout(() => {
          this.codeJustCopied = false;
        }, 2000);
      }
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  }

  getDataSourceTypeName(): string {
    if (!this.data.dataSource) return 'Unknown';
    
    switch (this.data.dataSource.dataSourceTypeId) {
      case 1: return 'API Listener';
      case 2: return 'Excel File';
      case 3: return 'CSV File';
      case 4: return 'Parquet File';
      case 5: return 'JSON File';
      default: return 'Unknown';
    }
  }

  canStartProject(): boolean {
    if (this.isApiListener) {
      // For API listeners, can always start (will auto-detect on first call)
      return true;
    } else if (this.isFileSource) {
      // For file sources, can start if we have an existing file or a new file selected
      return true;
    }
    return false;
  }

  getCurlCommand(): string {
    if (!this.endpoint || !this.token) {
      return '# Endpoint or token not available';
    }
    
    return `curl -X POST "${this.endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${this.token}" \\
  -d '/* your data here */'`;
  }

  getCSharpCode(): string {
    if (!this.endpoint || !this.token) {
      return '// Endpoint or token not available';
    }
    
    return `using System;
using System.Net.Http;
using System.Text;
using System.Text.Json;
using System.Threading.Tasks;

class ApiClient
{
    private static readonly string ApiEndpoint = "${this.endpoint}";
    private static readonly string BearerToken = "${this.token}";
    
    public static async Task<string> SendDataAsync(object data)
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");
        
        var json = JsonSerializer.Serialize(data);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        var response = await client.PostAsync(ApiEndpoint, content);
        
        response.EnsureSuccessStatusCode();
        return await response.Content.ReadAsStringAsync();
    }
    
    static async Task Main(string[] args)
    {
        // Example usage
        object myData = /* your data here */;
        var result = await SendDataAsync(myData);
        Console.WriteLine(result);
    }
}`;
  }

  getVBNetCode(): string {
    if (!this.endpoint || !this.token) {
      return '\'Endpoint or token not available';
    }
    
    return `Imports System
Imports System.Net.Http
Imports System.Text
Imports System.Text.Json
Imports System.Threading.Tasks

Module ApiClient
    Private Const ApiEndpoint As String = "${this.endpoint}"
    Private Const BearerToken As String = "${this.token}"
    
    Public Async Function SendDataAsync(data As Object) As Task(Of String)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")
            
            Dim json As String = JsonSerializer.Serialize(data)
            Dim content As New StringContent(json, Encoding.UTF8, "application/json")
            Dim response = Await client.PostAsync(ApiEndpoint, content)
            
            response.EnsureSuccessStatusCode()
            Return Await response.Content.ReadAsStringAsync()
        End Using
    End Function
    
    Async Function Main() As Task
        ' Example usage
        Dim myData As Object = Nothing ' your data here
        Dim result = Await SendDataAsync(myData)
        Console.WriteLine(result)
    End Function
End Module`;
  }

  getPythonCode(): string {
    if (!this.endpoint || !this.token) {
      return '# Endpoint or token not available';
    }
    
    return `import requests
import json
from typing import Any, Dict

API_ENDPOINT = "${this.endpoint}"
BEARER_TOKEN = "${this.token}"

def send_data(data: Any) -> Dict:
    """
    Send data to the API endpoint.
    
    Args:
        data: The data to send to the API
    
    Returns:
        The JSON response from the API
    """
    headers = {
        "Authorization": f"Bearer {BEARER_TOKEN}",
        "Content-Type": "application/json"
    }
    
    response = requests.post(API_ENDPOINT, headers=headers, json=data)
    response.raise_for_status()
    return response.json()

if __name__ == "__main__":
    # Example usage
    my_data = {}  # your data here
    result = send_data(my_data)
    print(result)`;
  }

  getJavaCode(): string {
    if (!this.endpoint || !this.token) {
      return '// Endpoint or token not available';
    }
    
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.google.gson.Gson;
import java.util.Map;

public class ApiClient {
    private static final String API_ENDPOINT = "${this.endpoint}";
    private static final String BEARER_TOKEN = "${this.token}";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();
    
    public static String sendData(Object data) throws Exception {
        String json = gson.toJson(data);
        
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(API_ENDPOINT))
            .header("Authorization", "Bearer " + BEARER_TOKEN)
            .header("Content-Type", "application/json")
            .POST(HttpRequest.BodyPublishers.ofString(json))
            .build();
        
        HttpResponse<String> response = client.send(request, 
            HttpResponse.BodyHandlers.ofString());
        
        if (response.statusCode() != 200) {
            throw new RuntimeException("API returned status: " + response.statusCode());
        }
        
        return response.body();
    }
    
    public static void main(String[] args) throws Exception {
        // Example usage
        Object myData = /* your data here */;
        String result = sendData(myData);
        System.out.println(result);
    }
}`;
  }

  getStatusCurlCommand(): string {
    if (!this.statusEndpoint || !this.token) return '# Endpoint or token not available';
    return `# Replace {ACTION_TOKEN} with the token returned from the start call
# Poll every few seconds until "status" is 2 (Complete) or 3 (Failed)
# Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
curl -X GET "${this.statusEndpoint}" \\
  -H "Authorization: Bearer ${this.token}"`;
  }

  getStatusCSharpCode(): string {
    if (!this.statusEndpoint || !this.token) return '// Endpoint or token not available';
    return `using System.Net.Http;
using System.Text.Json;

class StatusChecker
{
    private static readonly string BearerToken = "${this.token}";
    private static readonly string StatusUrl = "${this.statusEndpoint}";

    // Replace actionToken with the value returned from the start call
    // Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    public static async Task<JsonElement> PollUntilCompleteAsync(string actionToken)
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");

        while (true)
        {
            var url = StatusUrl.Replace("{ACTION_TOKEN}", actionToken);
            var response = await client.GetAsync(url);
            response.EnsureSuccessStatusCode();
            var json = await response.Content.ReadAsStringAsync();
            var result = JsonSerializer.Deserialize<JsonElement>(json);
            int status = result.GetProperty("status").GetInt32();

            if (status == 2) return result; // Complete
            if (status == 3) throw new Exception(
                result.GetProperty("errorMessage").GetString());

            await Task.Delay(3000); // Poll every 3 seconds
        }
    }
}`;
  }

  getStatusVBNetCode(): string {
    if (!this.statusEndpoint || !this.token) return "' Endpoint or token not available";
    return `Imports System.Net.Http
Imports System.Text.Json

Module StatusChecker
    Private Const BearerToken As String = "${this.token}"
    Private Const StatusUrl As String = "${this.statusEndpoint}"

    ' Replace actionToken with the value returned from the start call
    ' Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    Public Async Function PollUntilCompleteAsync(actionToken As String) As Task(Of JsonElement)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")

            Do While True
                Dim url As String = StatusUrl.Replace("{ACTION_TOKEN}", actionToken)
                Dim response = Await client.GetAsync(url)
                response.EnsureSuccessStatusCode()
                Dim json As String = Await response.Content.ReadAsStringAsync()
                Dim result As JsonElement = JsonSerializer.Deserialize(Of JsonElement)(json)

                ' Status: 0=Pending, 1=Running, 2=Complete, 3=Failed
                Dim statusCode As Integer = result.GetProperty("status").GetInt32()
                If statusCode = 2 Then Return result
                If statusCode = 3 Then
                    Throw New Exception(result.GetProperty("errorMessage").GetString())
                End If

                Await Task.Delay(3000) ' Poll every 3 seconds
            Loop
        End Using
    End Function
End Module`;
  }

  getStatusPythonCode(): string {
    if (!this.statusEndpoint || !this.token) return '# Endpoint or token not available';
    return `import requests
import time

BEARER_TOKEN = "${this.token}"
STATUS_URL = "${this.statusEndpoint}"

def poll_until_complete(action_token: str) -> dict:
    """
    Poll the status endpoint until the action is complete.
    Replace action_token with the value returned from the start call.
    Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    """
    headers = {"Authorization": f"Bearer {BEARER_TOKEN}"}
    url = STATUS_URL.replace("{ACTION_TOKEN}", action_token)

    while True:
        response = requests.get(url, headers=headers)
        response.raise_for_status()
        result = response.json()
        status = result["status"]

        if status == 2:  # Complete
            return result
        if status == 3:  # Failed
            raise Exception(f"Action failed: {result.get('errorMessage')}")

        time.sleep(3)  # Poll every 3 seconds`;
  }

  getStatusJavaCode(): string {
    if (!this.statusEndpoint || !this.token) return '// Endpoint or token not available';
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;

public class StatusChecker {
    private static final String BEARER_TOKEN = "${this.token}";
    private static final String STATUS_URL = "${this.statusEndpoint}";
    private static final HttpClient client = HttpClient.newHttpClient();

    // Replace actionToken with the value returned from the start call
    // Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    public static JsonObject pollUntilComplete(String actionToken) throws Exception {
        String url = STATUS_URL.replace("{ACTION_TOKEN}", actionToken);
        while (true) {
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + BEARER_TOKEN)
                .GET()
                .build();
            HttpResponse<String> response = client.send(request,
                HttpResponse.BodyHandlers.ofString());
            JsonObject result = JsonParser.parseString(response.body()).getAsJsonObject();
            int status = result.get("status").getAsInt();
            if (status == 2) return result;
            if (status == 3) throw new RuntimeException(
                "Action failed: " + result.get("errorMessage").getAsString());
            Thread.sleep(3000); // Poll every 3 seconds
        }
    }
}`;
  }

  getResultsCurlCommand(): string {
    if (!this.resultsEndpoint || !this.token) return '# Endpoint or token not available';
    return `# Call this once the status endpoint returns status 2 (Complete)
# Replace {ACTION_TOKEN} with the token returned from the start call
curl -X GET "${this.resultsEndpoint}" \\
  -H "Authorization: Bearer ${this.token}"`;
  }

  getResultsCSharpCode(): string {
    if (!this.resultsEndpoint || !this.token) return '// Endpoint or token not available';
    return `using System.Net.Http;

class ResultsFetcher
{
    private static readonly string BearerToken = "${this.token}";
    private static readonly string ResultsUrl = "${this.resultsEndpoint}";

    // Call this once the status endpoint returns status 2 (Complete)
    // Replace actionToken with the value returned from the start call
    public static async Task<string> GetResultAsync(string actionToken)
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");
        var url = ResultsUrl.Replace("{ACTION_TOKEN}", actionToken);
        var response = await client.GetAsync(url);
        response.EnsureSuccessStatusCode();
        return await response.Content.ReadAsStringAsync();
    }
}`;
  }

  getResultsVBNetCode(): string {
    if (!this.resultsEndpoint || !this.token) return "' Endpoint or token not available";
    return `Imports System.Net.Http

Module ResultsFetcher
    Private Const BearerToken As String = "${this.token}"
    Private Const ResultsUrl As String = "${this.resultsEndpoint}"

    ' Call this once the status endpoint returns status 2 (Complete)
    ' Replace actionToken with the value returned from the start call
    Public Async Function GetResultAsync(actionToken As String) As Task(Of String)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")
            Dim url As String = ResultsUrl.Replace("{ACTION_TOKEN}", actionToken)
            Dim response = Await client.GetAsync(url)
            response.EnsureSuccessStatusCode()
            Return Await response.Content.ReadAsStringAsync()
        End Using
    End Function
End Module`;
  }

  getResultsPythonCode(): string {
    if (!this.resultsEndpoint || !this.token) return '# Endpoint or token not available';
    return `import requests

BEARER_TOKEN = "${this.token}"
RESULTS_URL = "${this.resultsEndpoint}"

def get_result(action_token: str) -> dict:
    """
    Fetch the results once the status endpoint returns status 2 (Complete).
    Replace action_token with the value returned from the start call.
    """
    headers = {"Authorization": f"Bearer {BEARER_TOKEN}"}
    url = RESULTS_URL.replace("{ACTION_TOKEN}", action_token)
    response = requests.get(url, headers=headers)
    response.raise_for_status()
    return response.json()`;
  }

  getResultsJavaCode(): string {
    if (!this.resultsEndpoint || !this.token) return '// Endpoint or token not available';
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class ResultsFetcher {
    private static final String BEARER_TOKEN = "${this.token}";
    private static final String RESULTS_URL = "${this.resultsEndpoint}";
    private static final HttpClient client = HttpClient.newHttpClient();

    // Call this once the status endpoint returns status 2 (Complete)
    // Replace actionToken with the value returned from the start call
    public static String getResult(String actionToken) throws Exception {
        String url = RESULTS_URL.replace("{ACTION_TOKEN}", actionToken);
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + BEARER_TOKEN)
            .GET()
            .build();
        HttpResponse<String> response = client.send(request,
            HttpResponse.BodyHandlers.ofString());
        if (response.statusCode() != 200) {
            throw new RuntimeException("API returned status: " + response.statusCode());
        }
        return response.body();
    }
}`;
  }

  selectCodeText(event: MouseEvent): void {
    const preElement = event.target as HTMLPreElement;
    const range = document.createRange();
    range.selectNodeContents(preElement);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
}
